import React, { useState, useRef } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { publicRequest } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { GoogleReCaptcha, googleReCaptchaValidation } from './GoogleReCaptcha'
import { isValidPassword } from './_helpers/validation'
import CustomTextField from './_form_fields/CustomTextField'
import CustomBackdrop from './CustomBackdrop'

const PasswordResetConfirmWrapper = (props) => {
  const { userId, validationString, setLoginDialogOpen, setLoginNotification, setPasswordResetConfirmDialogOpen } = props
  const theme = useTheme()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [captchaValidated, setCaptchaValidated] = useState(false)
  const [errorCaptcha, setErrorCaptcha] = useState(false)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const captchaRef = useRef('')

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const formControlStyle = {
    background: theme.palette.azureishWhite,
    color: theme.palette.violetsAreBlue,
    borderRadius: '30px',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
  }

  const outlinedInputErrorStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `${theme.palette.error.main} 2px solid`,
      borderRadius: formControlStyle.borderRadius,
    },
  }

  const loginErrorHelperStyle = {
    color: theme.palette.congoPink,
    paddingLeft: '20px',
  }

  const loginHelperStyle = {
    color: 'white',
    paddingLeft: '20px',
  }

  const submitButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.violetsAreBlue,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.violetsAreBlue, 0.8),
    },
  }

  const handleChangePassword = (e) => {
    const value = e.target.value
    setPassword(value)
    if (isValidPassword(value)) {
      setErrors(prev => {return { ...prev, password: null }})
    }    
  }

  const handleChangeConfirmPassword = (e) => {
    const value = e.target.value
    setConfirmPassword(value)
    if (value === password) {
      setErrors(prev => {return { ...prev, confirmPassword: null }})
    }    
  }

  const scrollToTop = () => {
    const element = document.getElementById('formStart')
    element.scrollIntoView({ behavior: 'smooth' })
  }

  const passwordResetConfirmRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsersPasswordResetConfirm
      
    const request_data = { url, method: 'PUT', data: postData }
    
    const response = await publicRequest(request_data)
    
    if (response && response.status_code && response.status_code < 400 && response.data && response.data.status && response.data.status === 'reset_completed') {
      setPasswordResetConfirmDialogOpen(false)
      setLoginDialogOpen(true)
      setLoginNotification(defaultTexts.yourPasswordHasChanged)
    } else if (response && response.error && response.error.includes('User does not exist.')) {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.userNotExists
        }
      })
    } else {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.anErrorOccured,
        }
      })
    }
  }

  const handleClickSubmit = async () => {
    setIsLoading(true)
    let _errors = {}

    if (!password) {
      _errors.password = defaultTexts.pleaseFillOut
    } else if (!isValidPassword(password)) {
      _errors.password = defaultTexts.passwordRequirements
    }

    if (!confirmPassword) {
      _errors.confirmPassword = defaultTexts.pleaseFillOut
    } else if (confirmPassword !== password) {
      _errors.confirmPassword = defaultTexts.passwordDoesNotMatch
    }

    const captchaResponse = captchaValidated ? true : await googleReCaptchaValidation(captchaRef.current.getValue())
    setCaptchaValidated(captchaResponse ? true : false)

    if (Object.keys(_errors).length === 0 && captchaResponse) {
      const postData = {
        user_id: userId,
        validation_string: validationString,
        password,
        confirm_password: confirmPassword,
      }
      passwordResetConfirmRequest(postData)
    }
    
    if (Object.keys(_errors).length > 0) {
      scrollToTop()
      _errors.generalError = defaultTexts.anErrorOccured
      setErrors(_errors)
    }
    
    if (!captchaResponse) {
      setErrorCaptcha(true)
    }

    setIsLoading(false)
  }

  return (
    <Box id='formStart'>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      {
        errors.generalError &&
        <Alert severity='error' sx={{ fontSize: '16px' }}>{errors.generalError}</Alert>
      }

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='filled'
          label={defaultTexts.password}
          currentValue={password}
          setCurrentValue={handleChangePassword}
          required={true}
          loginError={errors.password ? true : false}
          loginHelperText={defaultTexts.passwordRequirements}
          loginErrorSx={outlinedInputErrorStyle}
          loginHelperSx={errors.password ? loginErrorHelperStyle : loginHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='filled'
          label={defaultTexts.confirmPassword}
          currentValue={confirmPassword}
          setCurrentValue={handleChangeConfirmPassword}
          required={true}
          loginError={errors.confirmPassword ? true : false}
          errorText={errors.confirmPassword}
          loginErrorSx={outlinedInputErrorStyle}
          loginErrorHelperSx={loginErrorHelperStyle}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={{ padding: '12px 0px 32px 0px' }}>
        <center>
          <GoogleReCaptcha
            ref={captchaRef}
            error={errorCaptcha}
            errorColor={theme.palette.congoPink}
            errorCenter={true}
          />
        </center>
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems='center'
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={1}
        sx={{ marginTop: '24px' }}
      >
        <Button
          variant='text'
          disableRipple
          disableElevation
          sx={submitButtonStyle}
          onClick={handleClickSubmit}
        >
          {defaultTexts.save}
        </Button>
      </Stack>
    </Box>
  )
}

export default PasswordResetConfirmWrapper